import type { QueryLanguageConfig } from "./types";

/**
 * Default, admin-tunable query language. Serialized to
 * `search/query-language.json` by the compiler; only regenerated when search
 * settings change (never on a nightly inventory rebuild).
 */
export const DEFAULT_QUERY_LANGUAGE: QueryLanguageConfig = {
  version: 1,

  hardStopWords: [
    "a", "an", "the", "and", "or", "but", "of", "to", "in", "on", "at", "by",
    "with", "from", "as", "be", "been", "being", "was", "were", "am", "are",
    "it", "its", "that", "these", "those", "there", "their", "them", "they",
    "he", "she", "his", "her", "him", "we", "us", "our", "ours", "you", "your",
    "yours", "i", "me", "my", "mine", "so", "than", "then", "too", "very",
    "into", "onto", "over", "about", "if", "will", "would", "shall", "should",
    "may", "might", "can", "could", "do", "does", "did", "done", "have", "has",
    "had", "not", "no", "yes", "any", "some", "all", "each", "every", "both",
    "other", "another", "more", "most", "much", "many", "again", "just",
  ],

  conversationFiller: [
    "please", "thanks", "thank", "hi", "hello", "hey", "ok", "okay", "well",
    "actually", "really", "maybe", "perhaps", "kinda", "sorta", "basically",
    "honestly", "guys", "folks", "sir", "maam", "asap", "quick", "quickly",
    "wondering", "wonder", "trying", "try", "want", "wanted", "wanting",
    "need", "needed", "needing", "get", "getting", "got", "let", "lets",
    "gonna", "wanna", "something", "anything", "someone", "anybody",
    "possible", "possibly", "available", "around", "maybe", "kind", "sort",
    "type", "types", "stuff", "thing", "things", "info", "information",
    "help", "helping", "shopping", "shop", "browse", "browsing", "buy",
    "dollar", "dollars", "usd", "bucks",
    "buying", "purchase", "purchasing", "price", "priced", "cost", "costs",
  ],

  requestPhrases: [
    "can you find me", "can you find", "can you show me", "can you show",
    "can you help me find", "could you find", "do you have any",
    "do you have a", "do you have", "do you carry any", "do you carry",
    "do you sell", "are you selling", "i am looking for", "im looking for",
    "i'm looking for", "we are looking for", "were looking for",
    "i am searching for", "im searching for", "i am trying to find",
    "im trying to find", "i would like", "id like", "i'd like",
    "i am interested in", "im interested in", "i need a", "i need an",
    "i need some", "i need", "we need", "show me some", "show me",
    "looking for a", "looking for", "find me a", "find me", "search for",
    "help me find", "how much is", "how much are", "what is the price of",
    "what do you have", "what have you got", "tell me about",
    "is there a", "are there any", "any chance you have",
  ],

  intentWords: {
    product_lookup: [
      "find", "have", "carry", "stock", "available", "looking", "search",
      "model", "unit", "trailer", "camper", "rv", "motorhome",
    ],
    parts_lookup: [
      "part", "parts", "hitch", "awning", "vent", "jack", "lift", "filter",
      "fitting", "seal", "sealant", "pump", "converter", "fridge",
      "refrigerator", "furnace", "thermostat", "battery", "wheel", "skin",
    ],
    price_inquiry: ["price", "cost", "payment", "monthly", "under", "below", "cheap", "budget"],
    service_request: ["service", "repair", "fix", "warranty", "appointment", "schedule", "winterize", "inspection"],
    information: ["how", "what", "why", "guide", "tips", "learn", "explain", "difference", "compare"],
    financing: ["finance", "financing", "loan", "credit", "apr", "lender", "prequalify"],
    rental: ["rent", "rental", "rentals", "renting"],
  },

  aliases: {
    "travel trailer": ["travel trailers", "traveltrailer", "traveltrailers", "tt"],
    "fifth wheel": ["fifth wheels", "5th wheel", "5th wheels", "fifthwheel", "5thwheel"],
    motorhome: ["motor home", "motor homes", "motorhomes", "coach", "class a", "class b", "class c"],
    "toy hauler": ["toy haulers", "toyhauler", "toyhaulers"],
    rv: ["rvs", "rv's", "recreational vehicle", "recreational vehicles"],
    "pop up": ["popup", "pop ups", "popups", "folding camper", "tent camper", "pop-up"],
    "destination trailer": ["park model", "destination trailers"],
    bunkhouse: ["bunk house", "bunk beds", "bunkbeds"],
    camper: ["campers"],
  },

  synonyms: {
    trailer: ["camper", "towable"],
    camper: ["trailer"],
    hitch: ["coupler", "towing"],
    awning: ["shade"],
    lift: ["ramp", "carrier"],
    used: ["preowned", "pre owned", "second hand"],
    new: ["brand new"],
    sleeps: ["sleep", "sleeper", "berths"],
    ac: ["air conditioner", "air conditioning"],
    generator: ["genset"],
    fridge: ["refrigerator"],
  },

  timeExpressions: {
    today: "relative_date",
    tomorrow: "relative_date",
    tonight: "relative_date",
    "this week": "relative_date",
    "next week": "relative_date",
    "this weekend": "relative_date",
    "next weekend": "relative_date",
    "this month": "relative_date",
    "next month": "relative_date",
    "this summer": "relative_season",
    "next summer": "relative_season",
    "this spring": "relative_season",
    "this fall": "relative_season",
    "this winter": "relative_season",
    "next year": "relative_year",
    "labor day": "holiday",
    "memorial day": "holiday",
    "fourth of july": "holiday",
    thanksgiving: "holiday",
    christmas: "holiday",
    "spring break": "holiday",
  },

  priceExpressions: {
    under: "max",
    below: "max",
    "less than": "max",
    "up to": "max",
    "no more than": "max",
    cheaper: "max",
    over: "min",
    above: "min",
    "more than": "min",
    "at least": "min",
    "starting at": "min",
    between: "range",
  },

  measurementExpressions: [
    "amp", "amps", "volt", "volts", "v", "watt", "watts", "btu", "lb", "lbs",
    "ft", "foot", "feet", "inch", "inches", "in", "gal", "gallon", "gallons",
    "psi", "mm", "cm", "oz",
  ],

  /**
   * Anything matching these is a protected identifier: never stemmed, never
   * dropped as filler or a stop word.
   */
  protectedPatterns: [
    "^(19|20)\\d{2}$",                 // years
    "^\\d{3,}$",                        // bare numeric ids: 65528
    "^[a-z]{1,4}\\d{2,}[a-z]{0,4}$",    // BX7421, BX738, 20FBS
    "^\\d{2,4}[a-z]{1,4}$",             // 227RK, 420LFT
    "^[a-z0-9]{11,17}$",                // VIN / long SKU
    "^\\d+[a-z]{1,3}-?\\d*$",           // 40043-P style part numbers
    "^\\$[\\d,.]+k?$",                  // dollar amounts
    "^\\d+(amp|v|w|btu|lb|lbs|ft|gal|in)$", // 50amp, 12v
  ],

  stemmingRules: {
    possessive: "'s|s'|’s",
    plural: "ies|es|s",
    ing: "ing",
    ed: "ed",
  },

  stemmingExceptions: [
    "gas", "class", "glass", "bus", "this", "is", "has", "was", "does", "goes",
    "series", "chassis", "axis", "lens", "news", "bias", "canvas", "atlas",
    "plus", "status", "focus", "bonus", "census", "brass", "grass", "pass",
    "cross", "less", "press", "mass", "boss", "loss", "dress", "express",
    "access", "success", "address", "process", "sales", "wheels", "means",
    "rvs", "as", "us", "its", "his", "hers", "yours", "always",
  ],

  knownPhrases: [
    "travel trailer", "fifth wheel", "toy hauler", "motor home", "class a",
    "class b", "class c", "pop up", "destination trailer", "park model",
    "rockwood geo pro", "geo pro", "palomino puma", "puma xle", "motorcycle lift",
    "bunk house", "front kitchen", "rear living", "rear bath", "outdoor kitchen",
    "murphy bed", "half ton", "solar package", "off road", "dry weight",
    "sleeps 8", "sleeps 6", "sleeps 10", "single axle", "double axle",
  ],

  rankingWeights: {
    exactQuery: 100,
    exactTitle: 95,
    exactModel: 95,
    exactManufacturer: 90,
    phraseInTitle: 85,
    manufacturerPlusModel: 80,
    allTokensInTitle: 70,
    structuredKeyword: 60,
    prefix: 35,
    description: 20,
    path: 10,
  },

  typeWeights: {
    inventory: 100,
    parts: 95,
    custom: 80,
    guide: 70,
    blog: 65,
    static: 60,
  },

  maxResults: 40,
  minQueryLength: 2,
};
